import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  useHasAnyElevatedPermissions,
  useHasFundIt,
  useHasManageMyGivingPage,
  useMemberToolbarDisplaying,
} from "Flocknote/stateHooks";

import useIsLoginActive from "Login/TwoFA/useIsLoginActive";
import { useIsMemberPortalRoute } from "MemberPortal/Public/hooks/useIsPageMatch";

import { useMemberToobarContext } from "./MemberToolbarState";
import { contentKeys } from "./contentKeys";
import { useMemberToolbarStore } from "./memberToolbarStore";

const useIsLoginDisabled = () => {
  const [isLoginActive] = useIsLoginActive();
  const { activeContentKey } = useMemberToolbarStore();

  if (activeContentKey === "login") return false;
  return isLoginActive;
};

const useHasActiveContent = () => {
  const { activeContentKey } = useMemberToolbarStore();

  const hasActiveContent = useMemo(
    () => activeContentKey !== "nothing",
    [activeContentKey]
  );

  return hasActiveContent;
};

const useEscapeAction = () => {
  const { activeContentKey } = useMemberToolbarStore();
  const { contactButtonRef, loginButtonRef, userButtonRef, backButtonRef } =
    useMemberToobarContext();

  return useCallback(() => {
    switch (activeContentKey) {
      case contentKeys.login:
        loginButtonRef.current.click();
        loginButtonRef.current.focus();
        return;
      case contentKeys.user:
        userButtonRef.current.click();
        userButtonRef.current.focus();
        return;
      case contentKeys.form:
        backButtonRef.current.click();
        backButtonRef.current.focus();
        return;
      case contentKeys.contact:
      case contentKeys.messageSent:
        contactButtonRef.current.click();
        contactButtonRef.current.focus();
        return;
      default:
        return;
    }
  }, [
    activeContentKey,
    contactButtonRef,
    loginButtonRef,
    userButtonRef,
    backButtonRef,
  ]);
};

const useShowMemberToolbar = () => {
  const [memberToolbarDisplaying, setMemberToolbarDisplaying] =
    useMemberToolbarDisplaying();
  const [oneWasAlreadyDisplaying] = useState(memberToolbarDisplaying); // static by design. If there was already one before this happen, don't show. Ever.

  useEffect(() => {
    setMemberToolbarDisplaying(true);
    return () => {
      if (!oneWasAlreadyDisplaying) setMemberToolbarDisplaying(false);
    };
  }, [setMemberToolbarDisplaying, oneWasAlreadyDisplaying]);

  const { isIniting } = useMemberToobarContext();

  return useMemo(() => {
    if (oneWasAlreadyDisplaying) return false;
    if (isIniting) return false;
    return true;
  }, [isIniting, oneWasAlreadyDisplaying]);
};

const useIsMemberPortal = () => {
  const isMemberPortal = useIsMemberPortalRoute();
  return isMemberPortal;
};

const useMemberPortalNavItems = () => {
  /*
  List of nav items that are available to the user.
  It is based on the user's permissions and the features that are available to the network.
  It is used to determine which nav items are displayed in the member toolbar.
  It is also used to determine the width of the member toolbar when the member portal nav is active.
  */
  const hasFundIt = useHasFundIt();
  const hasManageMyGivingPage = useHasManageMyGivingPage();
  const showGivingItem = hasManageMyGivingPage && hasFundIt;
  const hasAnyElevatedPermissions = useHasAnyElevatedPermissions();

  const navItems = {
    home: "HOME", // every member gets this
    notes: "NOTES", // every member gets this
  };

  if (showGivingItem) navItems.giving = "GIVING";
  if (hasAnyElevatedPermissions) navItems.admin = "ADMIN";

  return navItems;
};

export {
  useIsLoginDisabled,
  useHasActiveContent,
  useEscapeAction,
  useShowMemberToolbar,
  useIsMemberPortal,
  useMemberPortalNavItems,
};
